// Unpublished Work © 2023-2024 Deere & Company.

import { AccessToken } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import React, { ReactElement, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import useDealerApi from "../../api/dealer/useDealerApi";
import usePreferencesApi from "../../api/preferences/usePreferencesApi";
import useXtremeBffApi from "../../api/xtremeBff/useXtremeBff";
import withFullScreen from "../../components/hoc/withFullScreen/withFullScreen";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import { ConfigContext } from "../../context/ConfigContext";
import {
  DealerAccountProvider,
  dealerInfoLoader,
} from "../../context/DealerAccountContext";
import { useEffectiveUserId } from "../../context/DelegateContext/DelegateContext";
import { ErrorProvider } from "../../context/ErrorContext/ErrorContext";
import { FeatureToggleProvider } from "../../context/FeatureToggleContext/FeatureToggleContext";
import {
  FeatureToggleProviderV2,
  featureTogglesLoader,
} from "../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import {
  fetchAllPreferences,
  PreferenceProvider,
} from "../../context/PreferencesContext/PreferencesContext";
import { RBACAccessContext } from "../../context/RBACAccessContext/RBACAccessContext";
import { isFeatureToggleRBACEnabled } from "../../context/RBACAccessContext/RBACAcessUtil";
import {
  loadAccountsAndRoles,
  UserProvider,
} from "../../context/UserContext/UserContext";
import i18n from "../../i18n/i18n";
import { getAccountIdFromUrl } from "../../utils/urlFormatters";
import { ApplicationError, handleUserProviderErrors } from "../utils";

interface ProviderRegistration {
  Provider: (props: {
    children: ReactElement;
    initialValue?: any;
    isErrorOccurred?: any;
    error?: any;
  }) => JSX.Element;
  name: string;
  enabled: boolean;
  dataPreload: {
    enabled: boolean;
    fn: (token: string, accountId: string, userXid: string) => Promise<any>;
    errorFn?: (error: any) => ApplicationError;
  };
}

type ProviderDataPreLoadStatus = {
  [key: string]: {
    seedData: any;
    error: any;
    success: boolean;
  };
} & { loadComplete: boolean };

interface ProvidersFederationProps {
  children: React.ReactNode;
}

const identityFn = () => Promise.resolve();

// eslint-disable-next-line react/function-component-definition
const ProvidersFederation: React.FC<ProvidersFederationProps> = ({
  children,
}) => {
  const {
    ENABLE_PREFERENCES,
    ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
    ENABLE_DEALER_CONTEXT_V2,
    RBAC_SERVICE_URL,
    DEALER_SERVICE_URL,
    FEATURE_TOGGLE_SERVICE_URL,
    PREFERENCES_SERVICE_URL,
    ENABLE_ACCOUNT_LOCATION_FALLBACK,
    ENABLE_FT_RBAC_INTEGRATION,
    XTREME_BFF_SERVICE_URL,
    ENABLE_ALL_FEATURE_TOGGLE_STATUS,
    USE_BFF_PREFERENCES_ENDPOINT,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_FEATURE_TOGGLE_SERVICE,
    ENABLE_EMPTY_MAPPING_ACCOUNT_HANDLING,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_PREFERENCES,
  } = useContext(ConfigContext);

  const { pathname, search } = useLocation();

  const { authState } = useOktaAuth();
  const effectiveUserId = useEffectiveUserId();
  const {
    userDetails,
    featureToggleDetails,
    mappingAccounts,
    isDataLoaded,
    isRBACEnabled,
    isCorporateViewEnabled,
    hasMappingError,
  } = useContext(RBACAccessContext);

  const isDealerApiAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_DEALER_SERVICE;

  const dealerAPiHook = useDealerApi();
  const bffAPiHook = useXtremeBffApi();
  const usePreferencesApiHook = usePreferencesApi();
  const isPrefApiAuthEnabled =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_PREFERENCES;

  // The following provider will load in the order they are listed
  const providerRegistrations: ProviderRegistration[] = [
    {
      Provider: UserProvider,
      name: "UserProvider",
      enabled: isRBACEnabled,
      dataPreload: {
        enabled: true,
        fn: (token, _, userId) =>
          loadAccountsAndRoles(
            RBAC_SERVICE_URL,
            DEALER_SERVICE_URL,
            authState?.accessToken as AccessToken,
            userId,
            userDetails,
            mappingAccounts,
            ENABLE_FT_RBAC_INTEGRATION,
            isDealerApiAuthEnabled,
            dealerAPiHook
          ),
        errorFn: (onError) => handleUserProviderErrors(onError),
      },
    },
    {
      Provider: DealerAccountProvider,
      name: "DealerAccountProvider",
      enabled: true,
      dataPreload: {
        enabled: !isRBACEnabled,
        fn: ENABLE_DEALER_CONTEXT_V2
          ? (token, accountId) =>
              dealerInfoLoader(
                XTREME_BFF_SERVICE_URL,
                [accountId],
                token,
                i18n.language,
                ENABLE_ACCOUNT_LOCATION_FALLBACK,
                isDealerApiAuthEnabled,
                bffAPiHook
              )
          : identityFn,
      },
    },
    {
      Provider: FeatureToggleProvider, // will load second and so on....
      name: "FeatureToggleProvider",
      enabled: !ENABLE_FEATURE_TOGGLE_CONTEXT_V2 || !ENABLE_DEALER_CONTEXT_V2,
      dataPreload: { enabled: false, fn: identityFn },
    },
    {
      Provider: FeatureToggleProviderV2,
      name: "FeatureToggleProviderV2",
      enabled: ENABLE_FEATURE_TOGGLE_CONTEXT_V2 && ENABLE_DEALER_CONTEXT_V2,
      dataPreload: {
        enabled: !ENABLE_ALL_FEATURE_TOGGLE_STATUS,
        fn: (token) =>
          isFeatureToggleRBACEnabled(ENABLE_FT_RBAC_INTEGRATION)
            ? featureToggleDetails
            : featureTogglesLoader(
                FEATURE_TOGGLE_SERVICE_URL,
                token,
                ENABLE_RBAC_AUTH_SUPPORT &&
                  ENABLE_RBAC_AUTH_SUPPORT_FOR_FEATURE_TOGGLE_SERVICE
              ),
      },
    },
    {
      Provider: PreferenceProvider,
      name: "PreferenceProvider",
      enabled: ENABLE_PREFERENCES,
      dataPreload: {
        enabled: !isCorporateViewEnabled,
        fn: (token, accountId, userXid) =>
          fetchAllPreferences(
            PREFERENCES_SERVICE_URL,
            XTREME_BFF_SERVICE_URL,
            true,
            USE_BFF_PREFERENCES_ENDPOINT,
            [],
            accountId,
            userXid,
            false,
            token,
            ENABLE_EMPTY_MAPPING_ACCOUNT_HANDLING,
            isPrefApiAuthEnabled,
            usePreferencesApiHook,
            bffAPiHook
          ),
      },
    },
  ];

  const enabledRegistrations = providerRegistrations
    .slice()
    .reverse()
    .filter(({ enabled }) => enabled);

  const [dataPreLoadStatus, setDataPreLoadStatus] =
    useState<ProviderDataPreLoadStatus>(
      enabledRegistrations.reduce(
        (status, registration) => ({
          ...status,
          [registration.name]: {
            seedData: undefined,
            error: undefined,
            success: false,
          },
        }),
        { loadComplete: false } as ProviderDataPreLoadStatus
      )
    );

  const [dataPreLoadInProgress, setDataPreLoadInProgress] = useState<boolean>(
    enabledRegistrations.filter(({ dataPreload }) => dataPreload.enabled)
      .length > 0
  );

  const startLoading =
    (registrations: ProviderRegistration[]) =>
    (token: string, accountId: string, userXid: string) =>
      registrations.map(({ name, dataPreload: { enabled, fn } }) => {
        if (!enabled) {
          return Promise.resolve({
            name,
            seedData: undefined,
            success: true,
          });
        }
        return fn(token, accountId, userXid)
          .then((result) => ({
            name,
            seedData: result,
            success: true,
          }))
          .catch((error) => Promise.resolve({ name, error, success: false }));
      });

  const handleDataPreLoadComplete = (
    promisedResults: PromiseSettledResult<any>[]
  ) => {
    const loadStatus = promisedResults
      .map(
        (promisedResult) =>
          (promisedResult as PromiseFulfilledResult<any>).value
      )
      .reduce(
        (
          status,
          { name, success, seedData = undefined, error = undefined }
        ) => ({
          ...status,
          [name]: { seedData, error, success },
        }),
        { loadComplete: true }
      );
    setDataPreLoadStatus(loadStatus);
    return promisedResults;
  };

  useEffect(() => {
    const token = authState?.accessToken?.accessToken as string;
    const accountId = getAccountIdFromUrl(pathname, search) as string;

    if (hasMappingError && isDataLoaded && !dataPreLoadStatus.loadComplete) {
      setDataPreLoadStatus(
        (prev) => ({ ...prev, loadComplete: true } as ProviderDataPreLoadStatus)
      );
      setDataPreLoadInProgress(false);
      return;
    }

    if (
      token &&
      effectiveUserId &&
      isDataLoaded &&
      !dataPreLoadStatus.loadComplete
    ) {
      Promise.allSettled(
        startLoading(enabledRegistrations)(token, accountId, effectiveUserId)
      )
        .then(handleDataPreLoadComplete)
        .catch((error) => console.log(error))
        .finally(() => setDataPreLoadInProgress(false));
    }
  }, [
    authState,
    effectiveUserId,
    isDataLoaded,
    isRBACEnabled,
    hasMappingError,
  ]);

  if (dataPreLoadInProgress)
    return withFullScreen(<LoadingIndicator color="#367c2b" />);

  const errors = enabledRegistrations
    .filter(
      ({ name, dataPreload }) =>
        !!dataPreLoadStatus[name].error && dataPreload.errorFn
    )
    .map(
      ({ name, dataPreload }) =>
        dataPreload.errorFn &&
        dataPreload.errorFn(dataPreLoadStatus[name].error)
    )[0];
  return (
    <ErrorProvider initialValue={errors}>
      <>
        {enabledRegistrations.reduce(
          (childrenToWrap, { Provider, name }) => (
            <Provider
              initialValue={dataPreLoadStatus[name].seedData}
              isErrorOccurred={
                dataPreLoadStatus.loadComplete &&
                !dataPreLoadStatus[name].success
              }
              error={dataPreLoadStatus[name].error}
            >
              <>{childrenToWrap}</>
            </Provider>
          ),
          children
        )}
      </>
    </ErrorProvider>
  );
};

export default ProvidersFederation;
